import { lazy, Suspense } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { DevTagPreview } from '@/pages/DevTagPreview'
import { DevCustomizePreview } from '@/pages/DevCustomizePreview'
import { AuthProvider } from '@/hooks/useAuth'
import { useColorScheme } from '@/hooks/useColorScheme'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { OAuthRedirectGate } from '@/components/OAuthRedirectGate'
import { Spinner } from '@/components/Spinner'

// Route-level code splitting keeps the entry payload small — the tag landing
// page (the NFC deep-link target) doesn't ship the onboarding/account/post code.
const TagLandingPage = lazy(() =>
  import('@/pages/TagLandingPage').then((m) => ({ default: m.TagLandingPage })),
)
const AddPostPage = lazy(() =>
  import('@/pages/AddPostPage').then((m) => ({ default: m.AddPostPage })),
)
const OnboardingPage = lazy(() =>
  import('@/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
)
const TagsPage = lazy(() =>
  import('@/pages/TagsPage').then((m) => ({ default: m.TagsPage })),
)
const AccountPage = lazy(() =>
  import('@/pages/AccountPage').then((m) => ({ default: m.AccountPage })),
)
const ProfileSetupPage = lazy(() =>
  import('@/pages/ProfileSetupPage').then((m) => ({ default: m.ProfileSetupPage })),
)
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)
const FeedbackPage = lazy(() =>
  import('@/pages/FeedbackPage').then((m) => ({ default: m.FeedbackPage })),
)
const AuthActionPage = lazy(() =>
  import('@/pages/AuthActionPage').then((m) => ({ default: m.AuthActionPage })),
)
const AdminPage = lazy(() =>
  import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })),
)
const PassportPage = lazy(() =>
  import('@/pages/PassportPage').then((m) => ({ default: m.PassportPage })),
)
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
)
const MarketingPage = lazy(() =>
  import('@/pages/MarketingPage').then((m) => ({ default: m.MarketingPage })),
)

const RouteFallback = () => (
  <div className="flex min-h-dvh items-center justify-center bg-ink-900">
    <Spinner size={80} label="Loading" />
  </div>
)

/**
 * App root: global error boundary (festival connections fail constantly — never
 * a blank screen) wrapping the auth provider and the router.
 *
 * Route map mirrors the URL spec. `/t/:tagId` is the NFC / App Clip / Instant
 * App entry point and must carry no query params or session state.
 *
 *   /                  home: the user's tags list
 *   /t/:tagId          tag landing page (deep-link entry point)
 *   /t/:tagId/onboard  new-owner onboarding (entered when a tag is unprovisioned)
 *   /t/:tagId/post     add a post
 *   /account           profile + settings
 */
export const App = () => {
  // Keep the dark/light class in sync with the OS preference (no manual toggle).
  useColorScheme()
  return (
  <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <OAuthRedirectGate>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/t/:tagId" element={<TagLandingPage />} />
            <Route path="/t/:tagId/onboard" element={<OnboardingPage />} />
            <Route path="/t/:tagId/post" element={<AddPostPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/passport" element={<PassportPage />} />
            <Route path="/profile-setup" element={<ProfileSetupPage />} />
            <Route path="/u/:handle" element={<ProfilePage />} />
            <Route path="/about" element={<MarketingPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            {/* Firebase account-email landing (reset password, verify email). */}
            <Route path="/auth/action" element={<AuthActionPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/" element={<TagsPage marketingWhenCold />} />
            <Route path="/tags" element={<TagsPage />} />
            {/* DEV-only: visual harness for the Tag Page redesign (mock data). */}
            {import.meta.env.DEV && (
              <>
                <Route path="/__preview/tag" element={<DevTagPreview />} />
                <Route path="/__preview/customize" element={<DevCustomizePreview />} />
              </>
            )}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        </OAuthRedirectGate>
      </BrowserRouter>
    </AuthProvider>
  </ErrorBoundary>
  )
}
